import axios from '@/plugins/axios';
import storeutils from '@/stores/utils';
import {
    POLL_INTERVAL_MS,
    isRunTerminal,
    normalizeRun,
} from '@/composables/usePosSyncRunPolling';
import { normalizePosSyncRecord } from '@/composables/normalizePosSyncRecord';

const defaultListFilters = () => ({
    status: null,
    last_run_status: null,
    provider: null,
    establishment_id: null,
    search: '',
    ordering: '-last_finished_run_at',
    offset: 0,
    limit: 25,
});

const state = {
    list: [],
    listLoading: false,
    listFilters: defaultListFilters(),
    listCount: 0,
    listTotalCount: null,
    listTotalLoading: false,
    listStats: null,

    current: null,
    currentLoading: false,
    currentId: null,

    activeRun: null,
    activeRunLoading: false,
    pollingTimerId: null,
    pollingSyncId: null,
    pollingRunId: null,

    runs: [],
    runsLoading: false,
    runsCount: 0,
    runsPagination: { page: 1, pageSize: 20, ordering: '-started_at' },

    mappings: [],
    mappingsLoading: false,
    mappingsCount: 0,
    mappingsFetchId: 0,
    mappingsRequestKey: null,
    mappingFilters: {
        status: 'pending',
        catalog_id: null,
        pos_screen_name: null,
        min_score: null,
        missing_matching: null,
        suggestion_kind: null,
        search: '',
        ids: null,
        offset: 0,
        limit: 25,
    },
    mappingStats: { pending: 0, linked: 0, ignored: 0 },
    mappingScreenNames: [],

    dailySale: null,
    dailySaleLoading: false,
    closures: [],
    closuresLoading: false,
    closuresCount: 0,
    closuresFilters: {
        limit: 10,
        offset: 0,
        ordering: '-closed_at',
    },

    currentRunDetail: null,
    currentRunDetailLoading: false,

    connectionTest: null,
    connectionTestLoading: false,

    saving: false,
    providersAvailable: [
        { code: 'lightspeed', label: 'Lightspeed K-Series' },
        { code: 'laddition', label: "L'Addition" },
    ],

    appSettings: {
        simulation_mode: true,
        pilots_only: true,
        lightspeed_oauth_configured: false,
    },
    appSettingsLoading: false,
};

const mutations = {
    setList(state, { results, count }) {
        state.list = (results || []).map(normalizePosSyncRecord);
        state.listCount = count != null ? count : state.list.length;
    },
    setListLoading(state, loading) {
        state.listLoading = loading;
    },
    setListTotalCount(state, count) {
        state.listTotalCount = count;
    },
    setListTotalLoading(state, loading) {
        state.listTotalLoading = loading;
    },
    setListFilters(state, filters) {
        state.listFilters = { ...state.listFilters, ...filters };
    },
    setListStats(state, stats) {
        state.listStats = stats;
    },
    setCurrent(state, current) {
        state.current = current ? normalizePosSyncRecord(current) : current;
        if (state.current?.id != null) {
            state.currentId = state.current.id;
        }
        if (state.current?.active_run) {
            state.activeRun = state.current.active_run;
        }
        if (state.current?.mapping_stats) {
            state.mappingStats = state.current.mapping_stats;
        }
    },
    setCurrentLoading(state, loading) {
        state.currentLoading = loading;
    },
    setCurrentId(state, id) {
        state.currentId = id;
    },
    patchCurrent(state, patch) {
        if (state.current) {
            state.current = normalizePosSyncRecord({ ...state.current, ...patch });
        }
    },
    resetCurrent(state) {
        state.current = null;
        state.currentId = null;
        state.activeRun = null;
        state.mappings = [];
        state.mappingsCount = 0;
        state.mappingsRequestKey = null;
        state.mappingStats = { pending: 0, linked: 0, ignored: 0 };
    },
    setActiveRun(state, run) {
        state.activeRun = run;
    },
    setActiveRunLoading(state, loading) {
        state.activeRunLoading = loading;
    },
    setPollingTimerId(state, timerId) {
        state.pollingTimerId = timerId;
    },
    setPollingContext(state, { syncId, runId }) {
        state.pollingSyncId = syncId;
        state.pollingRunId = runId;
    },
    setRuns(state, { results, count }) {
        state.runs = results || [];
        state.runsCount = count != null ? count : state.runs.length;
    },
    setRunsLoading(state, loading) {
        state.runsLoading = loading;
    },
    setRunsPagination(state, pagination) {
        state.runsPagination = { ...state.runsPagination, ...pagination };
    },
    setMappings(state, { results, count }) {
        state.mappings = results || [];
        state.mappingsCount = count != null ? count : state.mappings.length;
    },
    clearMappings(state) {
        state.mappings = [];
        state.mappingsCount = 0;
        state.mappingScreenNames = [];
        state.mappingsRequestKey = null;
    },
    setMappingsFetchId(state, fetchId) {
        state.mappingsFetchId = fetchId;
    },
    setMappingsRequestKey(state, requestKey) {
        state.mappingsRequestKey = requestKey;
    },
    setMappingsLoading(state, loading) {
        state.mappingsLoading = loading;
    },
    setMappingFilters(state, filters) {
        state.mappingFilters = { ...state.mappingFilters, ...filters };
    },
    setMappingStats(state, stats) {
        state.mappingStats = stats || { pending: 0, linked: 0, ignored: 0 };
    },
    setMappingScreenNames(state, screenNames) {
        state.mappingScreenNames = Array.isArray(screenNames) ? screenNames : [];
    },
    setDailySale(state, dailySale) {
        state.dailySale = dailySale;
    },
    setDailySaleLoading(state, loading) {
        state.dailySaleLoading = loading;
    },
    setClosures(state, { results, count }) {
        state.closures = results || [];
        state.closuresCount = count != null ? count : state.closures.length;
    },
    setClosuresFilters(state, filters) {
        state.closuresFilters = {
            limit: 10,
            offset: 0,
            ordering: '-closed_at',
            ...filters,
        };
    },
    resetClosuresFilters(state) {
        state.closuresFilters = {
            limit: 10,
            offset: 0,
            ordering: '-closed_at',
        };
    },
    setClosuresLoading(state, loading) {
        state.closuresLoading = loading;
    },
    setCurrentRunDetail(state, run) {
        state.currentRunDetail = run;
    },
    setCurrentRunDetailLoading(state, loading) {
        state.currentRunDetailLoading = loading;
    },
    setConnectionTest(state, test) {
        state.connectionTest = test;
    },
    setConnectionTestLoading(state, loading) {
        state.connectionTestLoading = loading;
    },
    setSaving(state, saving) {
        state.saving = saving;
    },
    setAppSettings(state, settings) {
        state.appSettings = { ...state.appSettings, ...settings };
    },
    setAppSettingsLoading(state, loading) {
        state.appSettingsLoading = loading;
    },
};

function buildListParams(filters) {
    const params = {
        ordering: filters.ordering,
        limit: filters.limit,
        offset: filters.offset,
    };
    if (filters.status) {
        params.status = filters.status;
    }
    if (filters.last_run_status) {
        params.last_run_status = filters.last_run_status;
    }
    if (filters.provider) {
        params.provider = filters.provider;
    }
    if (filters.establishment_id) {
        params.establishment = filters.establishment_id;
    }
    if (filters.search) {
        params.search = filters.search;
    }
    return params;
}

function buildMappingParams(filters) {
    const params = {
        limit: filters.limit != null ? filters.limit : 25,
        offset: filters.offset != null ? filters.offset : 0,
    };
    if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
    }
    if (filters.catalog_id) {
        params.catalog_id = filters.catalog_id;
    }
    if (filters.pos_screen_name) {
        const screens = Array.isArray(filters.pos_screen_name)
            ? filters.pos_screen_name
            : [filters.pos_screen_name];
        const cleaned = screens
            .map((name) => String(name || '').trim())
            .filter(Boolean);
        if (cleaned.length) {
            params.pos_screen_name = cleaned;
        }
    }
    const minScore = Number(filters.min_score);
    if (!Number.isNaN(minScore) && minScore > 0) {
        params.min_score = minScore;
    }
    if (
        filters.missing_matching === true
        || filters.missing_matching === 'true'
        || filters.missing_matching === 1
        || filters.missing_matching === '1'
    ) {
        params.missing_matching = true;
    }
    const suggestionKind = String(filters.suggestion_kind || '').trim();
    if (suggestionKind === 'unique' || suggestionKind === 'ambiguous') {
        params.suggestion_kind = suggestionKind;
    }
    if (filters.search) {
        params.search = filters.search;
    }
    if (Array.isArray(filters.ids) && filters.ids.length) {
        params.ids = filters.ids.join(',');
    } else if (filters.ids) {
        params.ids = String(filters.ids);
    }
    return params;
}

function buildMappingsRequestKey(syncId, params) {
    return JSON.stringify([String(syncId), params]);
}

function normalizeConnectionTest(data) {
    if (!data) {
        return data;
    }
    if (typeof data.connected === 'boolean') {
        return {
            ...data,
            error: data.error || (data.connected ? null : data.message),
        };
    }
    const connected = data.ok === true;
    return {
        ...data,
        connected,
        error: connected ? null : data.message || data.error,
    };
}

const actions = {
    async fetchAppSettings({ commit }) {
        commit('setAppSettingsLoading', true);
        try {
            const response = await axios.get('/api/connectors/pos/settings/');
            commit('setAppSettings', response.data);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setAppSettingsLoading', false);
        }
    },

    async fetchListTotal({ commit }) {
        commit('setListTotalLoading', true);
        commit('setListTotalCount', null);
        try {
            const response = await axios.get('/api/connectors/pos/syncs/', {
                params: { limit: 0 },
            });
            const count = response.data.count ?? 0;
            commit('setListTotalCount', count);
            if (count === 0) {
                commit('setList', { results: [], count: 0 });
                commit('setListStats', null);
            }
            return count;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setListTotalLoading', false);
        }
    },

    async fetchList({ commit, state }) {
        commit('setListLoading', true);
        try {
            const response = await axios.get('/api/connectors/pos/syncs/', {
                params: buildListParams(state.listFilters),
            });
            const data = response.data;
            commit('setList', {
                results: data.results || data,
                count: data.count,
            });
            if (data.stats) {
                commit('setListStats', data.stats);
            }
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setListLoading', false);
        }
    },

    async fetchDetail({ commit, dispatch, state }, syncId) {
        const id = syncId || state.currentId;
        if (!id) {
            return null;
        }
        const idStr = String(id);
        const switchingSync = idStr !== String(state.currentId);
        if (switchingSync) {
            commit('clearMappings');
            commit('setMappingFilters', { status: null, catalog_id: null, search: '' });
        }
        commit('setCurrentId', id);
        const showPageLoading =
            !state.current || String(state.current.id) !== idStr;
        if (showPageLoading) {
            commit('setCurrent', null);
            commit('setCurrentLoading', true);
        }
        try {
            const response = await axios.get(`/api/connectors/pos/syncs/${id}/`, {
                params: {
                    expand: 'establishment,warehouse,catalogs,active_run,last_finished_run,mapping_stats',
                },
            });
            commit('setCurrent', response.data);
            if (response.data.lightspeed_oauth_configured != null) {
                commit('setAppSettings', {
                    lightspeed_oauth_configured: response.data.lightspeed_oauth_configured,
                });
            }
            if (response.data.mapping_stats) {
                commit('setMappingStats', response.data.mapping_stats);
            }
            if (response.data.active_run?.status === 'running' || response.data.active_run?.status === 'pending') {
                dispatch('startRunPolling', {
                    syncId: id,
                    runId: response.data.active_run.id,
                });
            }
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleResourceError(xhr_error);
        } finally {
            if (showPageLoading) {
                commit('setCurrentLoading', false);
            }
        }
    },

    async createPosSync({ commit }, payload) {
        commit('setSaving', true);
        try {
            const response = await axios.post('/api/connectors/pos/syncs/', payload);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setSaving', false);
        }
    },

    async updatePosSync({ commit, dispatch }, { id, data }) {
        commit('setSaving', true);
        try {
            const response = await axios.patch(`/api/connectors/pos/syncs/${id}/`, data);
            commit('patchCurrent', response.data);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setSaving', false);
        }
    },

    async activatePosSync({ dispatch }, syncId) {
        try {
            const response = await axios.post(`/api/connectors/pos/syncs/${syncId}/activate/`);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async deactivatePosSync({ dispatch }, syncId) {
        try {
            const response = await axios.post(`/api/connectors/pos/syncs/${syncId}/deactivate/`);
            await dispatch('stopRunPolling');
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async checkName({ commit }, name) {
        try {
            const response = await axios.get('/api/connectors/pos/syncs/check-name/', {
                params: { name },
            });
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async testConnection({ commit }, syncId) {
        commit('setConnectionTestLoading', true);
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/test-connection/`
            );
            const normalized = normalizeConnectionTest(response.data);
            commit('setConnectionTest', normalized);
            return normalized;
        } catch (xhr_error) {
            const error = storeutils.handleError(xhr_error);
            commit('setConnectionTest', { connected: false, error: error.message });
            throw error;
        } finally {
            commit('setConnectionTestLoading', false);
        }
    },

    async startLightspeedOAuth(_context, { syncId, returnPath }) {
        try {
            const response = await axios.get('/api/connectors/pos/lightspeed/oauth/start/', {
                params: {
                    sync_id: syncId,
                    return_path: returnPath,
                },
            });
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchLightspeedBusinessLocations(_context, syncId) {
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/business-locations/`
            );
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async saveLightspeedBusinessLocation({ dispatch }, { syncId, storeId, storeIds, storeLocations }) {
        try {
            const payload = {};
            if (Array.isArray(storeIds) && storeIds.length) {
                payload.store_ids = storeIds;
            } else if (storeId != null && storeId !== '') {
                payload.store_id = storeId;
            }
            if (Array.isArray(storeLocations)) {
                payload.store_locations = storeLocations;
            }
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/business-location/`,
                payload
            );
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchLightspeedAccountingGroups(_context, syncId) {
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/accounting-groups/`
            );
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async probeLightspeedImportFrom(_context, { syncId, from }) {
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/import-from-probe/`,
                { params: { from } }
            );
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async saveLightspeedAccountingGroup({ dispatch }, { syncId, accountingGroupId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/accounting-group/`,
                { default_accounting_group_id: accountingGroupId }
            );
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async saveLightspeedMenu({ dispatch }, { syncId, menuId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/menu/`,
                { default_menu_id: menuId || '' }
            );
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchLightspeedScreens(_context, { syncId, menuId }) {
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/screens/`,
                { params: { menu_id: menuId } }
            );
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async saveLightspeedCategoryScreen({ dispatch }, payload) {
        const {
            syncId,
            category,
            menuId,
            screenId,
            screenName,
        } = payload;
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/lightspeed/category-screen/`,
                {
                    category,
                    menu_id: menuId,
                    screen_id: screenId,
                    screen_name: screenName,
                }
            );
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async launchRun({ commit, dispatch }, { syncId, body }) {
        try {
            const response = await axios.post(`/api/connectors/pos/syncs/${syncId}/runs/`, body);
            const data = response.data;
            const runId = data.run_id || data.id;
            if (runId) {
                await dispatch('startRunPolling', { syncId, runId });
            }
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchRun({ commit, state }, { syncId, runId }) {
        commit('setActiveRunLoading', true);
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/runs/${runId}/`
            );
            if (state.pollingRunId && state.pollingRunId !== runId) {
                return response.data;
            }
            commit('setActiveRun', normalizeRun(response.data));
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setActiveRunLoading', false);
        }
    },

    async cancelRun({ commit, dispatch }, { syncId, runId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/runs/${runId}/cancel/`
            );
            await dispatch('pollActiveRunOnce', { syncId, runId });
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchRuns({ commit, state }, syncId) {
        const id = syncId || state.currentId;
        commit('setRunsLoading', true);
        try {
            const { page, pageSize, ordering } = state.runsPagination;
            const params = {
                limit: pageSize,
                offset: Math.max(0, (page - 1) * pageSize),
            };
            if (ordering) {
                params.ordering = ordering;
            }
            const response = await axios.get(`/api/connectors/pos/syncs/${id}/runs/`, {
                params,
            });
            const data = response.data;
            const results = (data.results || data).map((r) => normalizeRun(r));
            commit('setRuns', {
                results,
                count: data.count,
            });
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setRunsLoading', false);
        }
    },

    async fetchMappings({ commit, state }, syncId) {
        const id = syncId || state.currentId;
        if (!id) {
            return null;
        }
        const fetchId = state.mappingsFetchId + 1;
        commit('setMappingsFetchId', fetchId);
        const params = buildMappingParams(state.mappingFilters);
        const requestKey = buildMappingsRequestKey(id, params);
        // Rows already displayed answer another query: drop them so the table
        // shows its loading state instead of results the user did not ask for.
        if (requestKey !== state.mappingsRequestKey) {
            commit('setMappings', { results: [], count: 0 });
            commit('setMappingsRequestKey', requestKey);
        }
        commit('setMappingsLoading', true);
        try {
            const response = await axios.get(`/api/connectors/pos/syncs/${id}/mappings/`, {
                params,
            });
            if (fetchId !== state.mappingsFetchId) {
                return response.data;
            }
            if (String(id) !== String(state.currentId)) {
                return response.data;
            }
            const data = response.data;
            const results = (data.results || data || []).filter(
                (row) => String(row.pos_sync) === String(id)
            );
            commit('setMappings', {
                results,
                count: data.count != null ? data.count : results.length,
            });
            if (data.stats) {
                commit('setMappingStats', data.stats);
            }
            if (Array.isArray(data.screen_names)) {
                commit('setMappingScreenNames', data.screen_names);
            }
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            if (fetchId === state.mappingsFetchId) {
                commit('setMappingsLoading', false);
            }
        }
    },

    async linkMapping({ dispatch }, { syncId, body }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/`,
                body
            );
            await dispatch('fetchMappings', syncId);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async createProductMapping({ dispatch }, { syncId, body }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/create-product/`,
                body
            );
            await dispatch('fetchMappings', syncId);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async importMappingsCsv({ dispatch }, { syncId, file, catalogId, ignoreErrors }) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (catalogId) {
                formData.append('catalog_id', catalogId);
            }
            formData.append('ignore_errors', ignoreErrors ? '1' : '0');
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/import-csv/`,
                formData,
                {
                    params: { ignore_errors: ignoreErrors ? '1' : '0' },
                    headers: { 'Content-Type': 'multipart/form-data' },
                }
            );
            if (response.data.applied) {
                await dispatch('fetchMappings', syncId);
                await dispatch('fetchDetail', syncId);
            }
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async ignoreMapping({ dispatch }, { syncId, mappingId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/${mappingId}/ignore/`
            );
            await dispatch('fetchMappings', syncId);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async unignoreMapping({ dispatch }, { syncId, mappingId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/${mappingId}/unignore/`
            );
            await dispatch('fetchMappings', syncId);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async unlinkMapping({ dispatch }, { syncId, mappingId }) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/${mappingId}/unlink/`
            );
            await dispatch('fetchMappings', syncId);
            await dispatch('fetchDetail', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchPosArticles({ dispatch }, { syncId, body = {} }) {
        const id = typeof syncId === 'object' ? syncId?.syncId : syncId;
        const payload = typeof syncId === 'object' ? syncId?.body || {} : body;
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${id}/fetch-pos-articles/`,
                payload
            );
            const data = response.data;
            if (data.run_id) {
                await dispatch('startRunPolling', { syncId: id, runId: data.run_id });
            } else {
                await dispatch('fetchMappings', id);
            }
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async recomputeMappingSuggestions(_context, payload) {
        const syncId = typeof payload === 'object' && payload !== null
            ? payload.syncId
            : payload;
        const mappingIds = typeof payload === 'object' && payload !== null
            ? payload.mappingIds
            : undefined;
        const body = Array.isArray(mappingIds) && mappingIds.length
            ? { mapping_ids: mappingIds }
            : {};
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/mappings/recompute-suggestions/`,
                body
            );
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchDailySale({ commit }, syncId) {
        commit('setDailySaleLoading', true);
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/daily-sale/`
            );
            commit('setDailySale', response.data);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setDailySaleLoading', false);
        }
    },

    async fetchRunDetail({ commit }, { syncId, runId }) {
        commit('setCurrentRunDetailLoading', true);
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/runs/${runId}/`
            );
            const normalized = normalizeRun(response.data);
            commit('setCurrentRunDetail', normalized);
            return normalized;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setCurrentRunDetailLoading', false);
        }
    },

    async exportRunsCsv({ state }, syncId) {
        try {
            const params = {};
            const { ordering } = state.runsPagination;
            if (ordering) {
                params.ordering = ordering;
            }
            const response = await axios.get(
                `/api/connectors/pos/syncs/${syncId}/export-history/`,
                { params, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pos-sync-${syncId}-history.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async closeDailySale({ dispatch }, syncId) {
        try {
            const response = await axios.post(
                `/api/connectors/pos/syncs/${syncId}/close-daily-sale/`
            );
            await dispatch('fetchDailySale', syncId);
            return response.data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        }
    },

    async fetchClosures({ commit, state }, { syncId, params } = {}) {
        const id = syncId || state.currentId;
        if (!id) {
            return null;
        }
        commit('setClosuresLoading', true);
        try {
            const response = await axios.get(
                `/api/connectors/pos/syncs/${id}/daily-sale/closures/`,
                {
                    params: {
                        ...state.closuresFilters,
                        ...(params || {}),
                    },
                }
            );
            const data = response.data;
            commit('setClosures', {
                results: data.results || data,
                count: data.count,
            });
            return data;
        } catch (xhr_error) {
            throw storeutils.handleError(xhr_error);
        } finally {
            commit('setClosuresLoading', false);
        }
    },

    stopRunPolling({ commit, state }) {
        if (state.pollingTimerId) {
            clearInterval(state.pollingTimerId);
            commit('setPollingTimerId', null);
        }
        commit('setPollingContext', { syncId: null, runId: null });
    },

    async startRunPolling({ commit, dispatch, state }, { syncId, runId }) {
        dispatch('stopRunPolling');
        commit('setPollingContext', { syncId, runId });
        await dispatch('pollActiveRunOnce', { syncId, runId });
        if (!state.pollingRunId || state.pollingRunId !== runId) {
            return;
        }
        const timerId = setInterval(() => {
            dispatch('pollActiveRunOnce', { syncId, runId });
        }, POLL_INTERVAL_MS);
        commit('setPollingTimerId', timerId);
    },

    async pollActiveRunOnce({ commit, dispatch, state }, { syncId, runId }) {
        const sid = syncId || state.pollingSyncId;
        const rid = runId || state.pollingRunId;
        if (!sid || !rid) {
            return;
        }
        try {
            const run = await dispatch('fetchRun', { syncId: sid, runId: rid });
            if (run && isRunTerminal(run.status)) {
                dispatch('stopRunPolling');
                await dispatch('onRunFinished', { syncId: sid, run });
            }
        } catch (xhr_error) {
            dispatch('stopRunPolling');
            throw storeutils.handleError(xhr_error);
        }
    },

    async onRunFinished({ dispatch, state }, { syncId, run }) {
        const finished = run || state.activeRun;
        await dispatch('fetchDetail', syncId);
        await dispatch('fetchRuns', syncId);
        const actionType = finished?.action_type || finished?.action;
        if (actionType === 'fetch_articles' || actionType === 'full') {
            await dispatch('fetchMappings', syncId);
        }
        await dispatch('fetchDailySale', syncId).catch(() => {});
        await dispatch('fetchClosures', { syncId, params: { limit: 10 } }).catch(() => {});
        return finished;
    },

    resetDetail({ commit, dispatch }) {
        dispatch('stopRunPolling');
        commit('resetCurrent');
    },
};

const getters = {
    isCurrentActive: (state) => {
        const current = state.current;
        if (!current) {
            return false;
        }
        if (current.status) {
            return current.status === 'active';
        }
        return current.is_active === true;
    },
    canRunSync: (state, getter) => getter.isCurrentActive,
    isMappingComplete: (state) => !!state.current?.mapping_complete,
    hasLinkedCatalogs: (state) => (state.current?.linked_catalog_count || 0) > 0,
    isRunInProgress: (state) => {
        const status = state.activeRun?.status;
        return status === 'running' || status === 'pending';
    },
    lastFinishedRun: (state) => {
        const run = state.current?.last_finished_run;
        return run ? normalizeRun(run) : null;
    },
    showSimulationBanner: (state) => state.appSettings?.simulation_mode === true,
    isProductionInstance: (state) => state.appSettings?.simulation_mode === false,
    isLightspeedProductionReady: (state) =>
        state.appSettings?.lightspeed_production_enabled === true,
    showProductionBlockedBanner: (state, getters) =>
        getters.isProductionInstance && !getters.isLightspeedProductionReady,
    lightspeedOAuthConfigured: (state) =>
        !!state.appSettings?.lightspeed_oauth_configured
        || !!state.current?.lightspeed_oauth_configured,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
